import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { estimateTemplateEndpoints } from '@/lib/api/endpoints';
import { estimateTemplateKeys } from '@/lib/queryKeys';
import { useApiQuery } from '../useApiQuery';
import type { Attachment } from '@/types/attachment';

export function useEstimateTemplateAttachments(templateId: string) {
  return useApiQuery<Attachment[]>({
    queryKey: estimateTemplateKeys.attachments(templateId),
    queryFn: () =>
      apiGet<Attachment[]>(operationsClient, estimateTemplateEndpoints.attachments(templateId)),
    enabled: Boolean(templateId),
  });
}

export function useDeleteEstimateTemplateAttachment(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      apiDelete(
        operationsClient,
        estimateTemplateEndpoints.deleteAttachment(templateId, attachmentId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estimateTemplateKeys.attachments(templateId),
      });
    },
    onError: () => {
      toast.error('Failed to delete attachment');
    },
  });
}

export function useUploadEstimateTemplateAttachment(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation<Attachment, Error, { file: File; isInternal?: boolean }>({
    mutationFn: async ({ file, isInternal = false }) => {
      const res = await apiPost<{ uploadUrl: string; attachment: Attachment }>(
        operationsClient,
        estimateTemplateEndpoints.attachmentUploadUrl(templateId),
        {
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          isInternal,
        },
      );

      const s3Res = await fetch(res.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!s3Res.ok) {
        throw new Error(`Upload failed: ${s3Res.status} ${s3Res.statusText}`);
      }

      return res.attachment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estimateTemplateKeys.attachments(templateId),
      });
    },
    onError: () => {
      toast.error('Failed to upload attachment');
    },
  });
}
